
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SimpleRichTextEditor from "@/components/blog/SimpleRichTextEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, ArrowLeft, Plus, X, Video, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

interface Lesson {
  id: string;
  title: string;
  description: string;
  videoUrl: string | null;
  order: number;
}

interface CourseData {
  created_at: string;
  description: string;
  duration: string;
  id: string;
  image_url: string | null;
  level: string;
  price: string;
  published: boolean | null;
  status: string | null;
  title: string;
  updated_at: string;
  user_id: string;
  what_you_learn: string[] | null;
  text_direction?: "ltr" | "rtl"; // Mark as optional since it may not exist in the database yet
}

const EditCourse = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [level, setLevel] = useState("Beginner");
  const [price, setPrice] = useState("");
  const [duration, setDuration] = useState("");
  const [status, setStatus] = useState("Coming Soon");
  const [published, setPublished] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [whatYouLearn, setWhatYouLearn] = useState<string[]>(['']);
  const [textDirection, setTextDirection] = useState<"ltr" | "rtl">("ltr");
  
  const [lessons, setLessons] = useState<Lesson[]>([]);
  
  const courseImageInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    if (!id) return;
    
    const fetchCourse = async () => {
      try {
        setIsLoading(true);
        const { data: courseData, error } = await supabase
          .from("courses")
          .select("*")
          .eq("id", id)
          .single();
          
        if (error) throw error;
        
        if (courseData) {
          // Cast to our CourseData type but handle missing fields gracefully
          const data = courseData as CourseData;
          
          setTitle(data.title);
          setDescription(data.description);
          setLevel(data.level);
          setPrice(data.price);
          setDuration(data.duration);
          setStatus(data.status || "Coming Soon");
          setPublished(data.published || false);
          setImageUrl(data.image_url || "");
          
          // If text_direction exists in the data, use it, otherwise default to ltr
          setTextDirection(data.text_direction || "ltr");
          
          if (data.what_you_learn && Array.isArray(data.what_you_learn) && data.what_you_learn.length > 0) {
            setWhatYouLearn(data.what_you_learn);
          }
          
          const { data: lessonsData, error: lessonsError } = await supabase
            .from('course_lessons')
            .select('*')
            .eq('course_id', id)
            .order('lesson_order', { ascending: true });
            
          if (lessonsError) {
            console.error('Error fetching lessons:', lessonsError);
          } else if (lessonsData && lessonsData.length > 0) {
            const formattedLessons = lessonsData.map((lesson: any) => ({
              id: lesson.id,
              title: lesson.title || "",
              description: lesson.description || "",
              videoUrl: lesson.video_url || null,
              order: lesson.lesson_order || lessonsData.indexOf(lesson) + 1
            }));
            setLessons(formattedLessons);
          }
        }
      } catch (error: any) {
        console.error("Error fetching course:", error);
        toast({
          title: "Error",
          description: "Failed to load course: " + error.message,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCourse();
  }, [id, toast]);

  const addLearningItem = () => {
    setWhatYouLearn([...whatYouLearn, '']);
  };

  const removeLearningItem = (index: number) => {
    const updatedItems = [...whatYouLearn];
    updatedItems.splice(index, 1);
    setWhatYouLearn(updatedItems);
  };

  const updateLearningItem = (index: number, value: string) => {
    const updatedItems = [...whatYouLearn];
    updatedItems[index] = value;
    setWhatYouLearn(updatedItems);
  };
  
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImageFile(e.target.files[0]);
    }
  };
  
  const addLesson = () => {
    const newLesson: Lesson = {
      id: Date.now().toString(),
      title: "",
      description: "",
      videoUrl: null,
      order: lessons.length + 1
    };
    setLessons([...lessons, newLesson]);
  };

  const removeLesson = (lessonId: string) => {
    setLessons(lessons.filter(lesson => lesson.id !== lessonId));
  };

  const updateLesson = (id: string, field: keyof Lesson, value: any) => {
    setLessons(lessons.map(lesson => 
      lesson.id === id ? { ...lesson, [field]: value } : lesson
    ));
  };

  const createStorageBucket = async (bucketName: string) => {
    try {
      console.log(`Using bucket: ${bucketName}`);
      return true;
    } catch (error: any) {
      console.error(`Error with bucket ${bucketName}:`, error.message);
      return false;
    }
  };
  
  const uploadCourseImage = async (): Promise<string | null> => {
    if (!imageFile) return imageUrl;
    
    try {
      await createStorageBucket('course-images');
      
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${id}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;
      
      console.log("Uploading course image:", filePath);
      
      const { data, error } = await supabase.storage
        .from('course-images')
        .upload(filePath, imageFile, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (error) throw error;
      
      console.log("Course image uploaded successfully:", data);
      
      const { data: urlData } = supabase.storage
        .from('course-images')
        .getPublicUrl(filePath);
        
      console.log("Course image public URL:", urlData.publicUrl);
      return urlData.publicUrl;
    } catch (error: any) {
      console.error('Error uploading image:', error.message);
      toast({
        title: "Image upload failed",
        description: error.message,
        variant: "destructive"
      });
      return imageUrl;
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    
    try {
      setIsSaving(true);
      
      let updatedImageUrl = imageUrl;
      if (imageFile) {
        const uploadedUrl = await uploadCourseImage();
        if (uploadedUrl) updatedImageUrl = uploadedUrl;
      }
      
      const { error: updateError } = await supabase
        .from("courses")
        .update({
          title,
          description,
          level,
          price,
          duration,
          status,
          published,
          image_url: updatedImageUrl,
          what_you_learn: whatYouLearn.filter(item => item.trim() !== '').length > 0 ? whatYouLearn.filter(item => item.trim() !== '') : null,
          text_direction: textDirection, // Include text_direction in the update
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
        
      if (updateError) throw updateError;
      
      const { error: deleteError } = await supabase
        .from('course_lessons')
        .delete()
        .eq('course_id', id);
        
      if (deleteError) {
        console.error('Error deleting existing lessons:', deleteError);
      }
      
      if (lessons.length > 0) {
        for (const lesson of lessons) {
          const { error: lessonError } = await supabase
            .from('course_lessons')
            .insert({
              course_id: id,
              title: lesson.title,
              description: lesson.description,
              video_url: lesson.videoUrl,
              image_url: null,
              lesson_order: lesson.order
            });
            
          if (lessonError) {
            console.error('Error inserting lesson:', lessonError);
            toast({
              title: "Error saving lesson",
              description: lessonError.message,
              variant: "destructive",
            });
          }
        }
      }
      
      toast({
        title: "Success",
        description: "Course updated successfully",
      });
      
      navigate("/admin");
    } catch (error: any) {
      console.error("Error updating course:", error);
      toast({
        title: "Error",
        description: "Failed to update course: " + error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  if (isLoading) {
    return (
      <Layout title="Edit Course">
        <div className="max-w-4xl mx-auto px-4 py-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout title="Edit Course">
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <Link to="/admin" className="inline-flex items-center text-sm mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Link>
        
        <h1 className="text-3xl font-bold mb-8">Edit Course</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Course Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Course Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="level">Level</Label>
                  <Input
                    id="level"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration</Label>
                  <Input
                    id="duration"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="price">Price</Label>
                  <Input
                    id="price"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Coming Soon">Coming Soon</SelectItem>
                      <SelectItem value="Enrolling">Enrolling</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="textDirection">Text Direction</Label>
                <Select value={textDirection} onValueChange={(value: "ltr" | "rtl") => setTextDirection(value)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select text direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ltr">Left to Right (LTR)</SelectItem>
                    <SelectItem value="rtl">Right to Left (RTL)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500">Choose RTL for Arabic, Hebrew, or other right-to-left languages</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Card className="min-h-[300px]">
                  <CardContent className="p-0">
                    <SimpleRichTextEditor 
                      value={description} 
                      onChange={setDescription}
                      direction={textDirection}
                    />
                  </CardContent>
                </Card>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="whatYouLearn">What You'll Learn</Label>
                <div className="space-y-3">
                  {whatYouLearn.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={item}
                        onChange={(e) => updateLearningItem(index, e.target.value)}
                        placeholder="e.g., Master React fundamentals"
                      />
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeLearningItem(index)}
                        disabled={whatYouLearn.length === 1}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLearningItem}
                    className="mt-2"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Learning Item
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="image">Course Image</Label>
                {imageUrl && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-2">Current image:</p>
                    <img 
                      src={imageUrl} 
                      alt={title} 
                      className="h-48 object-cover rounded-md"
                    />
                  </div>
                )}
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  ref={courseImageInputRef}
                />
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Course Lessons</h2>
              <Button type="button" onClick={addLesson} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Lesson
              </Button>
            </div>
            
            {lessons.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center p-8 text-center">
                  <Video className="h-10 w-10 text-gray-400 mb-2" />
                  <p className="text-gray-500">No lessons added yet</p>
                  <Button type="button" onClick={addLesson} variant="outline" className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Lesson
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {lessons.map((lesson, index) => (
                  <Card key={lesson.id} className="relative">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={() => removeLesson(lesson.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    
                    <CardHeader>
                      <CardTitle className="text-lg">Lesson {index + 1}</CardTitle>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor={`lesson-title-${lesson.id}`}>Lesson Title</Label>
                        <Input
                          id={`lesson-title-${lesson.id}`}
                          value={lesson.title}
                          onChange={(e) => updateLesson(lesson.id, 'title', e.target.value)}
                          placeholder="Enter lesson title"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor={`lesson-desc-${lesson.id}`}>Description</Label>
                        <Textarea
                          id={`lesson-desc-${lesson.id}`}
                          value={lesson.description}
                          onChange={(e) => updateLesson(lesson.id, 'description', e.target.value)}
                          placeholder="Describe what this lesson covers..."
                          required
                          rows={3}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor={`lesson-video-url-${lesson.id}`}>
                          <div className="flex items-center gap-2">
                            <LinkIcon size={16} />
                            <span>Video URL</span>
                          </div>
                        </Label>
                        <Input
                          id={`lesson-video-url-${lesson.id}`}
                          value={lesson.videoUrl || ''}
                          onChange={(e) => updateLesson(lesson.id, 'videoUrl', e.target.value)}
                          placeholder="e.g., https://www.youtube.com/watch?v=..."
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2">
                <Switch
                  id="published"
                  checked={published}
                  onCheckedChange={setPublished}
                />
                <Label htmlFor="published">
                  Publish course
                </Label>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => navigate('/admin')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </Layout>
  );
};

export default EditCourse;
